// CompareChemicalView - Side-by-side chemical property comparison TABLE
import { h } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { compareCells, hiddenCells } from '../../../state/filterStore.js'
import { getCellFromExplorerCache, getCellNormalized } from '../../../modules/dataService.js'
import { CHEMICAL, replacePlaceholders, buildPlaceholderData } from '../../../data/definitions.js'
import CountryFlag from 'react-country-flag'

// Country code mapping for flags
const countryToISO = {
  'China': 'CN', 'Japan': 'JP', 'South Korea': 'KR', 'Korea': 'KR',
  'USA': 'US', 'United States': 'US', 'Germany': 'DE', 'France': 'FR',
  'UK': 'GB', 'United Kingdom': 'GB', 'Italy': 'IT', 'Netherlands': 'NL',
  'Sweden': 'SE', 'Switzerland': 'CH', 'Austria': 'AT', 'Poland': 'PL',
  'Spain': 'ES', 'Belgium': 'BE', 'Czech Republic': 'CZ', 'Finland': 'FI',
  'Norway': 'NO', 'Denmark': 'DK', 'Canada': 'CA', 'Australia': 'AU',
  'Brazil': 'BR', 'India': 'IN'
}

// Section icons (matching FactSheetSection style)
const ChemistryIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"></circle>
    <circle cx="12" cy="12" r="8" strokeDasharray="3 3"></circle>
    <line x1="12" y1="2" x2="12" y2="4"></line>
    <line x1="12" y1="20" x2="12" y2="22"></line>
    <line x1="2" y1="12" x2="4" y2="12"></line>
    <line x1="20" y1="12" x2="22" y2="12"></line>
  </svg>
)

const MaterialsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
)

const GeneralIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
)

// Info icon for definitions
const InfoIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
)

// Definition Tooltip Overlay
function DefinitionTooltip({ title, text, isOpen, onClose }) {
  if (!isOpen) return null
  
  return (
    <div className="definition-tooltip-overlay" onClick={onClose}>
      <div className="definition-tooltip-content" onClick={e => e.stopPropagation()}>
        <div className="definition-tooltip-header">
          <h4>{title}</h4>
          <button className="definition-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="definition-tooltip-body" dangerouslySetInnerHTML={{ __html: text }} />
      </div>
    </div>
  )
}

export default function CompareChemicalView() {
  const [cellDataArray, setCellDataArray] = useState([])
  const [loading, setLoading] = useState(true)
  const [openDefinition, setOpenDefinition] = useState(null)

  // Load full cell data
  useEffect(() => {
    async function loadCells() {
      const cellIds = compareCells.value
      if (!cellIds || cellIds.length === 0) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const cellsData = await Promise.all(
          cellIds.map(async (id) => {
            try {
              // Try explorer cache first — needs chemical detail fields not in cache,
              // so fall back to API. But if another compare view already loaded this
              // cell via getCellNormalized, apiGet's cache will serve it instantly.
              const cached = getCellFromExplorerCache(id)
              if (cached?.chemical?.housing !== undefined) return cached
              return await getCellNormalized(id, { fieldGating: 'demo' }) // Compare = browse: server-gate premium for demo
            } catch (err) {
              console.error(`Failed to load cell ${id}:`, err)
              return null
            }
          })
        )
        setCellDataArray(cellsData.filter(Boolean))
      } catch (err) {
        console.error('Failed to load compare cells:', err)
      } finally {
        setLoading(false)
      }
    }

    loadCells()
  }, [compareCells.value])

  if (loading) {
    return (
      <div className="compare-section-view loading">
        <div className="loading-spinner">Loading chemical data...</div>
      </div>
    )
  }

  if (cellDataArray.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>No cells selected for comparison</p>
          <p className="hint">Select 2-5 cells to compare chemical properties</p>
        </div>
      </div>
    )
  }

  // Filter out hidden cells
  const allCellDataArray = cellDataArray
  const visibleCellDataArray = allCellDataArray.filter(cell => {
    const cellId = cell.id || cell.product_id || cell.productId
    return !hiddenCells.value.includes(cellId)
  })

  // All cells hidden
  if (visibleCellDataArray.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>All cells are hidden</p>
          <p className="hint">Use the eye icon in the compare panel to show cells</p>
        </div>
      </div>
    )
  }

  // Build definitions from first cell for placeholder values
  const firstCell = visibleCellDataArray[0]
  const placeholderData = buildPlaceholderData(firstCell)
  
  const definitions = {
    chemistry: {
      key: 'chemistry',
      title: CHEMICAL.chemistry.title,
      text: replacePlaceholders(CHEMICAL.chemistry.text, placeholderData)
    },
    materials: {
      key: 'materials', 
      title: CHEMICAL.materials.title,
      text: replacePlaceholders(CHEMICAL.materials.text, placeholderData)
    }
  }

  // Table format for chemical comparison
  return (
    <div className="compare-section-view compare-table-view compare-chemical-table">
      {/* Definition Overlay */}
      <DefinitionTooltip 
        title={definitions[openDefinition]?.title}
        text={definitions[openDefinition]?.text}
        isOpen={!!openDefinition}
        onClose={() => setOpenDefinition(null)}
      />

      <div className="compare-table-container">
        <table className="compare-table">
          <thead>
            <tr>
              <th className="compare-table-header-label">Property</th>
              {visibleCellDataArray.map(cell => (
                <th key={cell.id} className="compare-table-header-cell">
                  <div className="cell-header-name">{cell.manufacturer}</div>
                  <div className="cell-header-model">{cell.name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Chemistry Section */}
            <tr className="section-header-row">
              <td colSpan={visibleCellDataArray.length + 1}>
                <span className="section-icon"><ChemistryIcon /></span>
                <span className="section-title">Chemistry</span>
                <button 
                  className="section-info-btn" 
                  onClick={() => setOpenDefinition(openDefinition === 'chemistry' ? null : 'chemistry')}
                  title="Show chemistry definition"
                >
                  <InfoIcon size={14} />
                </button>
              </td>
            </tr>
            <tr>
              <td className="compare-table-label">Technology</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">{cell?.technology || '–'}</td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Main Chemistry</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">{cell?.chemical?.main_chemistry || '–'}</td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Cathode Material</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">{cell?.chemical?.cathode || '–'}</td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Anode Material</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">{cell?.chemical?.anode || '–'}</td>
              ))}
            </tr>

            {/* Materials Section */}
            <tr className="section-header-row">
              <td colSpan={visibleCellDataArray.length + 1}>
                <span className="section-icon"><MaterialsIcon /></span>
                <span className="section-title">Materials</span>
                <button 
                  className="section-info-btn" 
                  onClick={() => setOpenDefinition(openDefinition === 'materials' ? null : 'materials')}
                  title="Show materials definition"
                >
                  <InfoIcon size={14} />
                </button>
              </td>
            </tr>
            <tr>
              <td className="compare-table-label">Housing Material</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">{cell?.chemical?.housing || '–'}</td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Anode Current Collector</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">{cell?.chemical?.anode_material_cc || '–'}</td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Cathode Current Collector</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">{cell?.chemical?.cathode_material_cc || '–'}</td>
              ))}
            </tr>

            {/* Cathode, Anode Section */}
          </tbody>
        </table>
      </div>
    </div>
  )
}
